/**
 * Exports — the preview, and the download URL.
 *
 * The download itself goes through `apiDownload` at the call site rather than
 * through a function here, because it is a file rather than JSON and the
 * screen needs to name the saved file. What this module owns is the pair of
 * things both the preview and the download have to agree on: the query, and
 * the dataset keys.
 */

import { apiGet, toQueryString } from './client';

/** The reports on offer. Mirrors `EXPORT_DATASETS` on the API. */
export const EXPORT_DATASETS = [
  'consolidated',
  'programs',
  'activities',
  'volunteers',
  'volunteer-activities',
] as const;

export type ExportDataset = (typeof EXPORT_DATASETS)[number];

export const EXPORT_FORMATS = ['xlsx', 'pdf'] as const;
export type ExportFormat = (typeof EXPORT_FORMATS)[number];

/**
 * The sheet a PDF will be laid out on, measured by the API.
 *
 * Not a choice. "Which sheet does a 37-column report need" has one right
 * answer, it is arithmetic, and the person downloading has no way to work it
 * out — so the API measures and the screen reports.
 */
export interface PaperChoice {
  size: string;
  /** 'A4', 'Letter' — what a person calls it. */
  label: string;
  orientation: 'portrait' | 'landscape';
  /** Points the columns need at their natural widths. This chose the sheet. */
  tableWidth: number;
  /** Points they take as drawn — smaller when trimmed to sit upright. */
  drawnWidth: number;
  /** Printable points this sheet offers the way the page is turned. */
  sheetWidth: number;
  /** True when even the largest sheet is too narrow and columns are grouped. */
  split: boolean;
}

/** `YYYY-MM-DD`, or '' for no bound. Both empty is the complete export. */
export type ExportQuery = {
  from?: string;
  to?: string;
  format?: ExportFormat;
};

export interface ExportPreview {
  dataset: ExportDataset;
  title: string;
  columns: string[];
  rows: (string | number | null)[][];
  /** Every row the window matches, not only the ones returned. */
  total: number;
  shown: number;
  /** What the PDF would be laid out on. Read-only — see `PaperChoice`. */
  paper: PaperChoice;
}

/**
 * The first rows of exactly what a download would contain.
 *
 * The API caps this; the screen reports `shown` against `total` rather than
 * assuming a number, so the cap can move without the caption becoming a lie.
 */
export function fetchExportPreview(
  dataset: ExportDataset,
  query: ExportQuery = {}
): Promise<ExportPreview> {
  return apiGet<ExportPreview>(`/exports/${dataset}/preview${toQueryString(query)}`);
}

/**
 * Where a download comes from.
 *
 * Built here rather than at the call site so the preview and the file are
 * asked for with the same query — the whole point of the preview is that it is
 * the file, and two places building the query is how that stops being true.
 */
export function exportPath(dataset: ExportDataset, query: ExportQuery = {}): string {
  return `/exports/${dataset}${toQueryString(query)}`;
}
